import React, { useState } from 'react';

const OrderForm = ({ onSubmit, onCancelAll, instrument, tickSize, validatePrice, roundToTick, userId, visibleInstruments, selectedOrderInstrument, setSelectedOrderInstrument }) => {
  const [side, setSide] = useState('BUY');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [orderType, setOrderType] = useState('LIMIT');
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userId || !quantity) {
      setStatus('❌ Please fill in User ID and Quantity');
      return;
    }

    if (orderType === 'LIMIT' && !price) {
      setStatus('❌ Please enter a price for limit orders');
      return;
    }

    if (orderType === 'STOP_LIMIT') {
      if (!stopPrice || !limitPrice) {
        setStatus('❌ Please enter both Stop Price and Limit Price for stop limit orders');
        return;
      }
    }

    setStatus('⏳ Submitting order...');

    // Generate client_order_id in YYYYMMDDHHMMSS format
    const now = new Date();
    const clientOrderId = now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, '0') +
      now.getDate().toString().padStart(2, '0') +
      now.getHours().toString().padStart(2, '0') +
      now.getMinutes().toString().padStart(2, '0') +
      now.getSeconds().toString().padStart(2, '0');

    const orderData = {
      user_id: userId,
      side: side,
      quantity: Number(quantity),
      price: orderType === 'MARKET' ? null : (orderType === 'STOP_LIMIT' ? null : Number(price)),
      order_type: orderType,
      symbol: instrument,  // Use dynamic instrument
      client_order_id: clientOrderId  // Add client order ID
    };

    // Add stop_price and limit_price for STOP_LIMIT orders
    if (orderType === 'STOP_LIMIT') {
      orderData.stop_price = Number(stopPrice);
      orderData.limit_price = Number(limitPrice);
      
      // Validate tick size for stop_price and limit_price
      if (!validatePrice(instrument, orderData.stop_price)) {
        setStatus(`❌ Invalid stop price ${orderData.stop_price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
      if (!validatePrice(instrument, orderData.limit_price)) {
        setStatus(`❌ Invalid limit price ${orderData.limit_price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
    }

    // Validate tick size for limit orders
    if (orderType === 'LIMIT' && orderData.price) {
      if (!validatePrice(instrument, orderData.price)) {
        setStatus(`❌ Invalid price ${orderData.price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
    }

    const result = await onSubmit(orderData);
    
    if (result.success) {
      setStatus('✅ Order submitted successfully');
      // Keep quantity and price values - only clear stop/limit prices for STOP_LIMIT orders
      if (orderType === 'STOP_LIMIT') {
        setStopPrice('');
        setLimitPrice('');
      }
    } else {
      setStatus(`❌ Order failed: ${result.message}`);
    }
  };

  const handleCancelAll = async () => {
    if (!userId) {
      setStatus('❌ Please enter User ID');
      return;
    }

    setStatus('⏳ Canceling all orders...');
    const result = await onCancelAll(userId);
    
    if (result.status === 'success' || result.success === true) {
      setStatus('✅ All orders canceled successfully');
    } else {
      setStatus(`❌ Cancel all failed: ${result.message}`);
    }
  };

  return (
    <div>
      <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px'}}>
        {visibleInstruments && visibleInstruments.length > 0 && (
          <select
            value={selectedOrderInstrument || ''}
            onChange={(e) => setSelectedOrderInstrument && setSelectedOrderInstrument(e.target.value)}
            style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', minWidth: '100px'}}
          >
            {visibleInstruments.map(symbol => (
              <option key={symbol} value={symbol}>{symbol}</option>
            ))}
          </select>
        )}
        <select 
          value={orderType} 
          onChange={(e) => setOrderType(e.target.value)}
          style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', minWidth: '100px'}}
        >
          <option value="LIMIT">Limit</option>
          <option value="MARKET">Market</option>
          <option value="STOP_LIMIT">Stop Limit</option>
        </select>
        <select 
          id="side"
          value={side} 
          onChange={(e) => setSide(e.target.value)}
          style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', minWidth: '80px'}}
        >
          <option value="BUY">Buy</option>
          <option value="SELL">Sell</option>
        </select>
        <input 
          id="quantity"
          placeholder="Qty" 
          type="number" 
          step="0.01" 
          min="0" 
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', flex: '1', minWidth: '100px'}}
        />
        {/* Regular price input for LIMIT orders */}
        {orderType === 'LIMIT' && (
          <div className="price-section" id="priceSection">
          <input 
            id="price"
            placeholder="Price" 
            type="number" 
            step={tickSize} 
            min="0" 
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', width: '120px'}}
          />
        </div>
        )}
        {/* Stop price and limit price inputs for STOP_LIMIT orders */}
        {orderType === 'STOP_LIMIT' && (
          <>
            <input 
              id="stopPrice"
              placeholder="Stop Price" 
              type="number" 
              step={tickSize} 
              min="0" 
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', width: '120px'}}
              title="Trigger price - order activates when price reaches this level"
            />
            <input 
              id="limitPrice"
              placeholder="Limit Price" 
              type="number" 
              step={tickSize} 
              min="0" 
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              style={{background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: '6px', height: '36px', width: '120px'}}
              title="Limit price - order executes at this price or better after trigger"
            />
          </>
        )}
        <button 
          id="submitBtn"
          className="btn" 
          type="button" 
          onClick={handleSubmit}
          style={{background: 'var(--accent)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', height: '36px', minWidth: '140px'}}
        >
          Submit {orderType} Order
        </button>
        <button 
          id="cancelAllBtn"
          className="btn" 
          type="button" 
          onClick={handleCancelAll}
          style={{background: 'var(--bad)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', height: '36px', minWidth: '120px'}}
        >
          Cancel All (User)
        </button>
      </div>

      {status && (
        <div className={`status ${status.includes('✅') ? 'ok' : status.includes('❌') ? 'error' : 'warn'}`} style={{padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', marginTop: '8px'}}>
          {status}
        </div>
      )}
    </div>
  );
};

export default OrderForm;